import {
  getClientOnlyField,
  maskEin,
  maskSsnLast4,
  buildContactFullName,
  sanitizePersonalAddress,
} from './fundingClientIntakeUtils';
import { formatCurrency, formatDate } from './fundingWorkspaceFormatters';

function pick(...values) {
  for (const value of values) {
    if (value != null && String(value).trim() !== '') return value;
  }
  return '';
}

function pickClientValue(client, ...keys) {
  const value = getClientOnlyField(client, keys);
  if (value != null && String(value).trim() !== '') return value;
  return '';
}

function maskDob(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const yearMatch = raw.match(/(\d{4})/);
  if (yearMatch) return `**/**/${yearMatch[1]}`;
  return '••/••/••••';
}

export function resolveClientIntakeDisplay(client) {
  const intake = client?.intake || {};
  const secure = client?._secure || {};
  const ssnRaw = pickClientValue(client, 'ssn') || pick(intake.ssn, secure.ssnFull);
  const einRaw = pickClientValue(client, 'ein') || pick(intake.ein, secure.einFull);
  const ssnLast4 = pickClientValue(client, 'ssnLast4', 'ssn_last4') || pick(intake.ssnLast4);
  const ssnInfo = ssnRaw
    ? maskSsnLast4(ssnRaw)
    : {
      masked: pickClientValue(client, 'ssnMasked', 'ssn_masked') || pick(intake.ssnMasked) || (ssnLast4 ? `***-**-${ssnLast4}` : ''),
      last4: ssnLast4,
      full: secure.ssnFull || '',
    };
  const einLast4 = pickClientValue(client, 'einLast4', 'ein_last4') || pick(intake.einLast4);
  const einInfo = einRaw
    ? maskEin(einRaw)
    : {
      masked: pickClientValue(client, 'einMasked', 'ein_masked') || pick(intake.einMasked) || (einLast4 ? `**-***${einLast4}` : ''),
      full: secure.einFull || '',
    };

  const firstName = pickClientValue(client, 'firstName', 'first_name') || pick(intake.firstName);
  const middleName = pickClientValue(client, 'middleName', 'middle_name') || pick(intake.middleName);
  const lastName = pickClientValue(client, 'lastName', 'last_name') || pick(intake.lastName);
  const fullName = buildContactFullName({ firstName, middleName, lastName })
    || pickClientValue(client, 'name')
    || '';

  const dob = pickClientValue(client, 'dob') || pick(intake.dob);
  const personalAddress = sanitizePersonalAddress(
    getClientOnlyField(client, ['personalAddress', 'personal_address']),
  );

  return {
    firstName,
    middleName,
    lastName,
    fullName: fullName || '—',
    email: pickClientValue(client, 'email') || pick(intake.email),
    phone: pickClientValue(client, 'phone') || pick(intake.phone),
    status: pickClientValue(client, 'status') || pick(intake.status),
    source: pickClientValue(client, 'sourceLabel', 'source') || pick(intake.source),
    ownerName: fullName || pick(intake.ownerName),
    ownership: pickClientValue(client, 'ownershipPercentage', 'ownership_percentage') || pick(intake.ownership),
    ssnMasked: ssnInfo.masked || (ssnInfo.last4 ? `***-**-${ssnInfo.last4}` : ''),
    ssnFull: ssnInfo.full || secure.ssnFull || '',
    ssnLast4: ssnInfo.last4 || ssnLast4 || '',
    hasClientSsn: Boolean(client?.hasClientSsn || ssnLast4 || client?.ssnLast4),
    dob,
    dobMasked: maskDob(dob),
    personalAddress,
    timeLiveInHouse: pickClientValue(client, 'timeLiveInHouse', 'time_live_in_house') || pick(intake.timeLiveInHouse),
    housingPayment: pickClientValue(client, 'housingPayment', 'housing_payment') || pick(intake.housingPayment),
    businessLegalName: pickClientValue(client, 'businessName', 'business_name', 'legalBusinessName', 'legal_business_name', 'businessLegalName')
      || pick(intake.businessLegalName)
      || null,
    einMasked: einInfo.masked || (einLast4 ? `**-***${einLast4}` : ''),
    einFull: einInfo.full || einRaw,
    einLast4: einLast4 || '',
    hasClientEin: Boolean(client?.hasClientEin || einLast4 || client?.einLast4),
    businessStartDate: pickClientValue(client, 'businessStartDate', 'business_start_date') || pick(intake.businessStartDate),
    businessAddress: pickClientValue(client, 'businessAddress', 'business_address') || pick(intake.businessAddress),
    businessPhone: pickClientValue(client, 'businessPhone', 'business_phone') || pick(intake.businessPhone),
    businessEmail: pickClientValue(client, 'businessEmail', 'business_email') || pick(intake.businessEmail),
    industry: pickClientValue(client, 'industry') || pick(intake.industry),
    entityType: pickClientValue(client, 'entityType', 'entity_type') || pick(intake.entityType),
    personalIncome: pickClientValue(client, 'personalIncome', 'personal_income') || pick(intake.personalIncome),
    businessIncome: pickClientValue(client, 'businessIncome', 'business_income') || pick(intake.businessIncome),
    employer: pickClientValue(client, 'employer') || pick(intake.employer),
    timeWorkingAt: pickClientValue(client, 'timeWorkingAt', 'time_working_at') || pick(intake.timeWorkingAt),
    monthlyRevenue: pickClientValue(client, 'monthlyRevenue', 'monthly_revenue') || pick(intake.monthlyRevenue),
    monthlyDeposits: pickClientValue(client, 'monthlyDeposits', 'monthly_deposits') || pick(intake.monthlyDeposits),
    businessExpenses: pickClientValue(client, 'businessExpenses', 'business_expenses') || pick(intake.businessExpenses),
    savings: pickClientValue(client, 'savings') || pick(intake.savings),
    retirement: pickClientValue(client, 'retirementAccount', 'retirement_account', 'retirement') || pick(intake.retirement),
    bankName: pickClientValue(client, 'bankName', 'bank_name') || pick(intake.bankName),
    experian: pickClientValue(client, 'experianScore', 'experian_score') || pick(intake.experian, intake.experianScore),
    transUnion: pickClientValue(client, 'transunionScore', 'transunion_score') || pick(intake.transUnion, intake.transunionScore),
    equifax: pickClientValue(client, 'equifaxScore', 'equifax_score') || pick(intake.equifax, intake.equifaxScore),
    highestLimit: pickClientValue(client, 'currentHighestLimit', 'current_highest_limit') || pick(intake.highestLimit),
    inquiries: pickClientValue(client, 'openInquiries', 'open_inquiries') || pick(intake.inquiries),
    utilization: pickClientValue(client, 'utilizationPercent', 'utilization_percent') || pick(intake.utilization),
    derogatoryItems: pickClientValue(client, 'derogatoryItems', 'derogatory_items') || pick(intake.derogatoryItems),
    readiness: pickClientValue(client, 'readinessPercent', 'readiness_percent', 'readinessScore', 'readiness_score')
      || pick(intake.readiness),
    desiredFunding: pickClientValue(client, 'requestedAmount', 'requested_amount') || pick(intake.desiredFunding),
    targetProduct: pickClientValue(client, 'targetProduct', 'target_product') || pick(intake.targetProduct),
    fundingStage: pickClientValue(client, 'fundingStage', 'funding_stage') || pick(intake.fundingStage),
    applicationType: pickClientValue(client, 'applicationType', 'application_type') || pick(intake.applicationType),
    personalFundingAmount: pickClientValue(client, 'personalFundingAmount', 'personal_funding_amount') || pick(intake.personalFundingAmount),
    zeroPercentAmount: pickClientValue(client, 'zeroPercentCreditCardAmount', 'zero_percent_credit_card_amount') || pick(intake.zeroPercentAmount),
    businessLocAmount: pickClientValue(client, 'businessLocAmount', 'business_loc_amount') || pick(intake.businessLocAmount),
    targetLender: pickClientValue(client, 'targetLender', 'target_lender') || pick(intake.targetLender),
    tags: pickClientValue(client, 'tags') || pick(intake.tags),
    advisorOwner: pickClientValue(client, 'advisorOwner', 'advisor_owner') || pick(intake.advisorOwner),
    notes: pickClientValue(client, 'notes') || pick(intake.notes),
    applicationEmailCreated: pickClientValue(client, 'applicationEmailCreated', 'application_email_created')
      || pick(intake.applicationEmailCreated),
    hasApplicationPasswordCreated: Boolean(client?.hasApplicationPasswordCreated),
    applicationPasswordCreatedMasked: client?.hasApplicationPasswordCreated
      ? (client?.applicationPasswordCreatedMasked || '••••••••')
      : '',
  };
}

export function formatMoneyField(value) {
  if (value === '' || value == null) return '';
  const numeric = Number(String(value).replace(/[^\d.]/g, ''));
  if (Number.isFinite(numeric) && String(value).match(/[\d]/)) {
    return formatCurrency(numeric);
  }
  return String(value);
}

export function formatDateField(value) {
  if (!value) return '';
  return formatDate(value);
}
